import axios from 'axios';

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const BASE_URL = String(process.env.REACT_APP_REPORTS_BASE_URL || '').replace(/\/+$/, '');

const LOGIN_REPORT_POINTS = 0;
const LOGIN_REPORT_TIME = '00:00';

const uuid4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

/** Pull report id from game-server responses (shape varies by endpoint). */
function extractReportId(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const candidates = [
    payload.id,
    payload.reportId,
    payload.report_id,
    payload.data?.id,
    payload.data?.reportId,
    payload.data?.report_id,
    payload.result?.id,
    payload.result?.reportId,
    payload.report?.id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return null;
}

async function refreshToken(currentToken) {
  const { data } = await axios.get(`${BASE_URL}/auth/rf-token`, {
    headers: {
      'client-secret': CLIENT_SECRET,
      'Content-Type': 'application/json',
      ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
      'Idempotency-Key': uuid4(),
    },
  });
  return data.token;
}

/**
 * Inserts the player's platform report — score 0, time 00:00. Add only:
 * the orb never updates this report afterwards. Returns the new reportId.
 */
async function addLoginReport({ isGuest, authToken, sessionId, organizationId, userId, gameId, name, email }) {
  const headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    'Idempotency-Key': uuid4(),
  };

  if (isGuest) {
    const { data } = await axios.post(`${BASE_URL}/game/add/guest/score`, {
      points: LOGIN_REPORT_POINTS,
      time: LOGIN_REPORT_TIME,
      gameId,
      name: name || '',
      sessionId,
      userId,
      organizationId,
      ans: '',
    }, { headers });
    return extractReportId(data);
  }

  const body = {
    sessionId,
    organizationId,
    userId,
    gameId,
    name: name || '',
    ...(email ? { email } : {}),
    points: LOGIN_REPORT_POINTS,
    total_score: LOGIN_REPORT_POINTS,
    time: LOGIN_REPORT_TIME,
  };
  const url = `${BASE_URL}/game-server/report/add`;
  try {
    const { data } = await axios.post(url, body, { headers: { ...headers, Authorization: `Bearer ${authToken}` } });
    return extractReportId(data);
  } catch (err) {
    if (err.response?.status !== 401) throw err;
    const newToken = await refreshToken(authToken);
    const { data } = await axios.post(url, body, {
      headers: { ...headers, Authorization: `Bearer ${newToken}`, 'Idempotency-Key': uuid4() },
    });
    return extractReportId(data);
  }
}

function backend(sessionToken) {
  return axios.create({
    baseURL: String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, ''),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
    timeout: 20000,
  });
}

/**
 * Sends the login report once per player: if the backend already has a
 * report for this user it does nothing; otherwise it inserts one and records it.
 * Never throws — reporting must not block the game.
 */
export async function sendLoginReportOnce({ sessionToken, ...player }) {
  if (!player.userId) return;
  try {
    const api = backend(sessionToken);
    const { data: status } = await api.post('/orbReportStatus', { userId: player.userId });
    if (status?.reported) return;

    const reportId = await addLoginReport(player);
    if (!reportId) throw new Error('Report API returned no reportId');
    await api.post('/orbReportSaved', {
      userId: player.userId,
      reportId,
      name: player.name,
      email: player.email,
    });
  } catch (err) {
    console.error('sendLoginReportOnce:', err?.response?.data || err.message);
  }
}
